import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { useLoadUserProfileViewModel } from "./useLoadUserProfileViewModel";
import {
	SaveUserProfileImagesModel,
	UpdateUserProfileImagesResponse,
	UserIdentityModel,
	saveImages
} from "client/Api/UserProfile/service";
import { useUserProfileStore } from "./userProfileStore";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	FloatingActionButton,
	ToolbarAction,
	useSaveChangesDialog
} from "client/Components";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { hasMaxSize, isSupportedFileType } from "client/Validation/rules";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { UserProfileDetails } from "./UserProfileDetails";
import {
	CombinedInputValidationResult,
	InputValidationResultErrorSource
} from "client/Validation/state";
import { IValueInputAttachable } from "client/Forms/attachable";
import { PathRedirect } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { SettingsPage } from "../SettingsPage";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { useEditRestrictionsMoreInfo } from "./useEditRestrictionsMoreInfo";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";

export type UserProfilePageProps = {
	onNavigate: NavigationCallback;
};

export const UserProfilePage = (props: UserProfilePageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [formData, setFormData] = useState<FormData>(DEFAULT_IMAGE_FORM_DATA);

	const [currentAvatarId, setCurrentAvatarId] = useState<string | null>(null);
	const [currentBackgroundImageId, setCurrentBackgroundImageId] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const userTimezone: TimeZone = useUserTimezone();

	const {
		viewModel,
		status
	} = useLoadUserProfileViewModel();

	useEffect(
		(): void => {
			if (!viewModel) {
				return;
			}

			const {
				avatarId,
				backgroundImageId
			} = viewModel.model;

			setCurrentAvatarId(avatarId);
			setCurrentBackgroundImageId(backgroundImageId);
		},
		[viewModel]
	);

	const setCurrentUserIdentity: (patch: Partial<Omit<UserIdentityModel, "userId">>) => void = useUserProfileStore(x => x.setCurrentUserIdentity);

	useActiveSettingsMenuItem(SettingsPageKind.UserProfile);

	const [saveChangesDialog, openSaveChangesDialog] = useSaveChangesDialog();

	const validationConfiguration = useMemo(
		(): IValidationConfiguration<FormData> | undefined => {
			if (!viewModel) {
				return undefined;
			}

			const {
				avatarSelectorOptions,
				backgroundImageSelectorOptions
			} = viewModel;

			const result: IValidationConfiguration<FormData> = createValidationConfiguration();

			result.forScalar("avatar")
				.add(
					hasMaxSize(
						avatarSelectorOptions.maxFileSize,
						localize("_UploadUserProfilePictureError")
					),
					isSupportedFileType(
						avatarSelectorOptions.supportedImageTypes,
						localize("_UploadUserProfilePictureError")
					)
				);

			result.forScalar("backgroundImage")
				.add(
					hasMaxSize(
						backgroundImageSelectorOptions.maxFileSize,
						localize("_UploadUserProfileCoverImageError")
					),
					isSupportedFileType(
						backgroundImageSelectorOptions.supportedImageTypes,
						localize("_UploadUserProfileCoverImageError")
					)
				);

			return result;
		},
		[viewModel]
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration ?? "NoValidation"
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [moreInfoHandler, moreInfoElement] = useEditRestrictionsMoreInfo(viewModel?.editRestrictions);

	const showValidationErrorAsAlert = useCallback(
		(attachable: IValueInputAttachable<unknown>): void => {
			const message: string | undefined = getDirectValidationMessage(attachable);

			if (isNotNilOrEmpty(message)) {
				alertManager.showError(message);
			}
		},
		[alertManager]
	);

	const saveImagesHandler = useCallback(
		async (): Promise<void> => {
			if (status !== "ready" || isSaving) {
				return;
			}

			try {
				setIsSaving(true);

				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsSaving(false);

					showValidationErrorAsAlert(
						formHandler.for("avatar")
					);

					showValidationErrorAsAlert(
						formHandler.for("backgroundImage")
					);

					return;
				}

				const model: SaveUserProfileImagesModel = formData;
				const response: UpdateUserProfileImagesResponse = await saveImages(model, globalCancellation);

				if (isNotNil(response.newAvatarId)) {
					setCurrentUserIdentity({
						avatarId: response.newAvatarId
					});

					setCurrentAvatarId(response.newAvatarId);
				}

				if (isNotNil(response.newBackgroundImageId)) {
					setCurrentBackgroundImageId(response.newBackgroundImageId);
				} else {
					setCurrentBackgroundImageId(null);
				}

				setFormData(DEFAULT_IMAGE_FORM_DATA);

				alertManager.showSuccess(localize("_ChangesSaved"));

				setIsSaving(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsSaving(false);

				if (formHandler.validation
					.tryProcessServerError(e, pathRedirects)
				) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			status,
			isSaving,
			formHandler,
			globalCancellation,
			formData,
			alertManager,
			showValidationErrorAsAlert,
			setCurrentUserIdentity
		]
	);

	const resetImagesHandler = useCallback(
		(): void => {
			setFormData(DEFAULT_IMAGE_FORM_DATA);

			formHandler.validation
				.clearServerErrors();

			formHandler.validation
				.validate();
		},
		[formHandler.validation]
	);

	const hasUnsavedImages: boolean = formData.avatar.isSet
		|| formData.backgroundImage.isSet;

	const editHandler = useCallback(
		async (): Promise<void> => {
			if (!hasUnsavedImages) {
				onNavigate(userProfileRouting.edit);
				return;
			}

			const confirmation: TryResult<boolean> = await openSaveChangesDialog();

			if (!confirmation.wasSuccessful) {
				return;
			}

			if (confirmation.result) {
				await saveImagesHandler();
			} else {
				resetImagesHandler();
			}

			onNavigate(userProfileRouting.edit);
		},
		[
			hasUnsavedImages,
			onNavigate,
			openSaveChangesDialog,
			resetImagesHandler,
			saveImagesHandler
		]
	);

	const defaultActions: readonly UserProfilePageAction[] = useMemo(
		() => {
			return listDefaultActions({
				editHandler,
				saveImagesHandler,
				resetImagesHandler
			});
		},
		[
			resetImagesHandler,
			saveImagesHandler,
			editHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly UserProfilePageAction[] => {
			return buildActionsForTopBar<UserProfilePageContext, UserProfilePageAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const actionContext = useMemo(
		(): UserProfilePageContext => {
			return {
				hasUnsavedImages,
				isResponsive
			};
		},
		[hasUnsavedImages, isResponsive]
	);

	return (
		<SettingsPage
			hasBusyIndicator={status === "loading" || isSaving}
			actions={topBarActions}
			actionContext={actionContext}
			title={localize("_UserProfile")}
			onMoreInfo={moreInfoHandler}
		>
			<SettingsPage.Section>
				{isNotNil(viewModel) && (
					<UserProfileDetails
						viewModel={viewModel}
						imageFormHandler={formHandler}
						currentAvatarId={currentAvatarId}
						currentBackgroundImageId={currentBackgroundImageId}
						timezone={userTimezone}
					/>
				)}
			</SettingsPage.Section>

			{moreInfoElement}

			<AiAssistantFabSpacing />

			<FloatingActionButton
				icon="pen"
				onClick={editHandler}
				trackingId="fab-edit-user-profile"
			/>

			{saveChangesDialog}
		</SettingsPage>
	);
};
UserProfilePage.displayName = "UserProfilePage";

function getDirectValidationMessage(attachable: IValueInputAttachable<unknown>): string | undefined {
	const { validation } = attachable;

	const validationState: CombinedInputValidationResult = validation.getValidationState();
	if (validationState.kind === InputValidationResultKind.Valid) {
		return undefined;
	}

	if (validationState.errorSource === InputValidationResultErrorSource.ChildError) {
		return undefined;
	}

	return validationState.errorMessage;
}

const pathRedirects: readonly PathRedirect[] = [
	{
		pattern: new DotPathWildcardPattern("Avatar.*?.*?"),
		replacement: ""
	},
	{
		pattern: new DotPathWildcardPattern("BackgroundImage.*?.*?"),
		replacement: ""
	}
];

export const UserProfilePageForRouter = connectToRouter(
	UserProfilePage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type UserProfilePageContext = {
	isResponsive: boolean;
	hasUnsavedImages: boolean;
};

type UserProfilePageAction = ToolbarAction<UserProfilePageContext>;

type DefaultActionsHandlers = {
	editHandler(): void;
	saveImagesHandler(): void;
	resetImagesHandler(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly UserProfilePageAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (ctx) => !ctx.isResponsive,
			execute: () => handlers.editHandler(),
			icon: "pen",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "save-images",
			title: localize("_Save"),
			canExecute: (ctx) => ctx.hasUnsavedImages,
			execute: () => handlers.saveImagesHandler(),
			icon: "floppy-disk"
		},
		{
			kind: ActionKind.Regular,
			name: "cancel-images",
			title: localize("_Cancel"),
			canExecute: (ctx) => ctx.hasUnsavedImages,
			execute: () => handlers.resetImagesHandler(),
			icon: "xmark"
		}
	];
}

export type FormData = OverrideProperties<
	SaveUserProfileImagesModel,
	{
		avatar: OptionalModel<Base64FileUriUploadWithMetadataModel>;
		backgroundImage: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
	}
>;

const DEFAULT_IMAGE_FORM_DATA: FormData = {
	avatar: { isSet: false },
	backgroundImage: { isSet: false }
};

export const userProfileRouting = {
	page: "/UserProfile",
	edit: "/UserProfile/Edit"
};